import CustomIcon from "components/CustomIcon";
import Image from "next/image";
import { useDispatch } from "react-redux";
import { highlightSentences, clearHighlight } from "lib/redux/highlightSlice";
import type { SectionType } from "lib/redux/highlightSlice";

export const LongSentences = ({
  longSentences,
  section = "experience",
  itemIndex = null,
}: {
  longSentences: string[];
  section?: SectionType;
  itemIndex?: number | null;
}) => {
  const dispatch = useDispatch();
  const hasIssues = longSentences.length > 0;

  // Handle hover on individual long sentence
  const handleSentenceMouseEnter = (sentence: string) => {
    dispatch(
      highlightSentences({
        section,
        itemIndex,
        sentences: [sentence], 
      })
    );
  };

  const handleMouseLeave = () => {
    dispatch(clearHighlight());
  };

  return (
    <div className="flex flex-col mt-4 md:mt-3 gap-[10px] rounded-xl bg-[#FBF9F9] px-[15px] py-[10px] lg:gap-[16px] lg:px-[20px] lg:py-[14px] relative">
      
      <div className="absolute top-[-16px] font-bold left-4 lg:left-8 bg-white text-black px-4 lg:px-6 py-2 rounded-xl shadow-md flex items-center gap-2">
        <Image
          src="/icons/score-check/Announcement.svg"
          height={24}
          width={24}
          alt="icon"
          className="h-4 w-4 lg:h-5 lg:w-5"
        />
        <span className="text-sm lg:text-base xl:text-lg font-bold">{hasIssues ? "Long Sentences" : "No Long Sentences"}</span>
      </div>

      <div className="mt-6">
        {hasIssues ? (
          <>
            <ul className="flex max-w-full list-disc flex-col items-start px-[3px] marker:text-base marker:text-[#92C5FF] sm:px-[5px] sm:marker:text-lg lg:marker:text-2xl">
              {longSentences.map((mistake, idx) => {
                const words = mistake.trim().split(/\s+/);
                const truncated = words.length > 12 ? words.slice(0, 12).join(" ") + "..." : mistake;
                return(
                <li
                  key={idx}
                  className="my-[6px] w-full pl-2 xl:pl-3 flex items-center gap-2 bg-[#FFDCDC] rounded-lg py-2 px-3 whitespace-break-spaces text-xs font-normal text-[#000000] cursor-pointer transition-opacity hover:opacity-80 md:text-base lg:my-[6px] lg:gap-4 lg:text-base xl:my-[10px] "
                  onMouseEnter={() => handleSentenceMouseEnter(mistake)}
                  onMouseLeave={handleMouseLeave}
                >
                  {/* <div className="h-auto w-auto">
                    <CustomIcon iconName="cross" className="h-3 w-3 lg:h-4 lg:w-4" />
                  </div> */}
                  {truncated}
                </li>
              )})}
            </ul>
            <div className="flex flex-col items-center justify-center mt-4 gap-3">
              <div className="relative w-32 h-32 lg:w-40 lg:h-40">
                <Image 
                  src="/assets/resumeScore/LongSentence.png"
                  alt="Long sentences illustration"
                  fill
                  className="object-contain"
                />
              </div>
              <p className="text-center text-sm lg:text-base text-red-600 font-semibold">
                long sentences aren't always better
              </p>
              <p className="text-center text-xs lg:text-sm text-gray-600">
                One impactful line is better than a long, unclear sentence.
              </p>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-4">
            <div className="relative w-32 h-32 lg:w-40 lg:h-40">
              <Image 
                src="/assets/resumeScore/Impactful.png"
                alt="No long sentences"
                fill
                className="object-contain"
              />
            </div>
            <p className="text-center text-sm lg:text-base text-green-600 font-semibold">
              Perfect sentence length!
            </p>
            <p className="text-center text-xs lg:text-sm text-gray-600">
              Your sentences are concise and impactful.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
